import { type Interval, isWithinInterval } from 'date-fns';

import {
  type AkaVeranstaltungId,
  experteBlockungQuelle,
  type ExperteFromResourcesRouter,
  type Experteplanung,
  geschaeftsbereiche,
  hiveExperteTerminStatus,
  Zeitraum,
} from '../../../../../dtos';
import { getExperteName, getIntervalAlignedToStartAndEndOfDay } from '../../../../../utils';
import { type CalendarEvent, CalendarExperteEvent } from '../CalendarEvent';
import { calendarResourceTypes } from '../calendarResourceTypes';
import { AbstractCalendarResource } from './AbstractCalendarResource';
import {
  eventCreationStrategies,
  type EventCreationStrategy,
  type IntervalWithOptionalVeranstaltung,
} from './AbstractCalendarResource.types.js';

export class CalendarExperteResource extends AbstractCalendarResource {
  public readonly type = calendarResourceTypes.EXPERTE;

  public readonly id: `e-${string}`;

  public readonly name: string;

  public readonly experte: ExperteFromResourcesRouter;

  public readonly skipExperteGueltigkeitsCheck: boolean;

  public readonly eventCreationStrategy: EventCreationStrategy =
    eventCreationStrategies.SEPARATE_EVENTS_FOR_ALL_ABLAUFTAGE;

  public constructor(
    experte: ExperteFromResourcesRouter,
    jahr: number,
    expertePool: Experteplanung[],
    skipExperteGueltigkeitsCheck: boolean,
  ) {
    super();
    this.experte = experte;
    this.id = `e-${experte.experteSapId}`;
    this.name = this.calculateExperteRowName(jahr, expertePool);
    this.skipExperteGueltigkeitsCheck = skipExperteGueltigkeitsCheck;
  }

  private calculateExperteRowName(jahr: number, expertePool: Experteplanung[]): string {
    let label = getExperteName(this.experte);

    const auslastungImJahr = this.experte.experteAuslastungen.find(
      (experteAuslastung) => experteAuslastung.jahr === jahr,
    );
    if (auslastungImJahr) {
      // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
      label += ' / ' + Math.round(auslastungImJahr.summeTage) + '/' + Math.round(auslastungImJahr.schwelleRot);
    }

    const expertePlanungsinformation = expertePool.find(
      (expertePlanung) => expertePlanung.experteSapId === this.experte.experteSapId,
    );

    if (!expertePlanungsinformation) {
      return label;
    }

    return label;
  }

  public createFixedEventsFromBlockungen(akaVeranstaltungId: AkaVeranstaltungId | null): CalendarEvent[] {
    const events: CalendarExperteEvent[] = [];

    for (const blockung of this.experte.experteBlockungen) {
      if (blockung.geloescht && !(blockung.veranstaltung?.akaVeranstaltungId === akaVeranstaltungId)) {
        continue;
      }

      for (const interval of blockung.zeitraeume) {
        const newEvent = this.createEventFromBlockungAndInterval(blockung, interval);
        newEvent.isOldSelection = blockung.veranstaltung?.akaVeranstaltungId === akaVeranstaltungId;
        newEvent.allDay = true;
        events.push(newEvent);
      }
    }

    return events;
  }

  public createPendingEventsFromBlockungen(akaVeranstaltungId: AkaVeranstaltungId): CalendarEvent[] {
    const events: CalendarExperteEvent[] = [];

    for (const blockung of this.experte.experteBlockungen.filter(
      (_blockung) => _blockung.veranstaltung?.akaVeranstaltungId == akaVeranstaltungId,
    )) {
      for (const interval of blockung.zeitraeume) {
        const newEvent = this.createEventFromBlockungAndInterval(blockung, interval);
        newEvent.isCurrentSelection = true;
        newEvent.allDay = true;
        newEvent.resize = false;
        newEvent.editable = true;
        events.push(newEvent);
      }
    }

    return events;
  }

  public createPendingEventsFromSuggestions(
    suggestedEvents: { tage: { zeitraum: Zeitraum; expertenSapIds: number[] }[] },
    akaVeranstaltungId: AkaVeranstaltungId | null,
  ) {
    const events: CalendarExperteEvent[] = [];

    for (const suggestedEventTag of suggestedEvents.tage) {
      if (suggestedEventTag.expertenSapIds.includes(this.experte.experteSapId)) {
        const newEvent = new CalendarExperteEvent(
          suggestedEventTag.zeitraum.start,
          suggestedEventTag.zeitraum.end,
          this,
          {
            onlineTool: null,
            quelleTerminId: akaVeranstaltungId ?? '',
            veranstaltung: null,
            zeitraeume: [suggestedEventTag.zeitraum],
            geschaeftsbereich: geschaeftsbereiche.UNKNOWN,
            quelle: experteBlockungQuelle.VAMOS,
            geloescht: false,
            status: hiveExperteTerminStatus.GEPLANT,
          },
        );
        newEvent.resize = false;
        newEvent.editable = true;
        newEvent.isOldSelection = false;
        newEvent.isCurrentSelection = true;
        newEvent.allDay = true;
        events.push(newEvent);
      }
    }

    return events;
  }

  private createEventFromBlockungAndInterval(
    blockung: (typeof this.experte.experteBlockungen)[number],
    interval: Interval<Date, Date>,
  ) {
    return new CalendarExperteEvent(interval.start, interval.end, this, {
      locationKuerzel: blockung.locationKuerzel,
      onlineTool: blockung.onlineTool,
      quelleTerminId: blockung.quelleTerminId,
      veranstaltung: blockung.veranstaltung,
      zeitraeume: blockung.zeitraeume,
      geschaeftsbereich: blockung.geschaeftsbereich,
      quelle: blockung.quelle,
      geloescht: blockung.geloescht,
      status: blockung.status,
    });
  }

  public createNewCalendarEventByInterval(interval: Interval<Date, Date>): CalendarExperteEvent {
    const event = new CalendarExperteEvent(interval.start, interval.end, this, {
      // unimportant values
      locationKuerzel: null,
      onlineTool: null,
      quelleTerminId: '',
      veranstaltung: null,
      geschaeftsbereich: geschaeftsbereiche.KFF,
      quelle: experteBlockungQuelle.VAMOS,
      geloescht: false,

      // important values
      zeitraeume: [interval],
      status: hiveExperteTerminStatus.GEPLANT,
    });
    event.isCurrentSelection = true;

    return event;
  }

  public getRessourceSapId(): number {
    return this.experte.experteSapId;
  }

  public isBookableAt(date: Date): boolean {
    return !this.experte.experteBlockungen.some(
      (blockung) =>
        !blockung.geloescht &&
        blockung.zeitraeume.some((zeitraum) => isWithinInterval(date, getIntervalAlignedToStartAndEndOfDay(zeitraum))),
    );
  }

  protected getBlockungen(): IntervalWithOptionalVeranstaltung[] {
    return this.experte.experteBlockungen;
  }

  public gueltigkeitenToEnforce(): Zeitraum[] {
    const gueltigkeiten = this.experte.gueltigkeitszeitraeume;

    if (this.skipExperteGueltigkeitsCheck || !gueltigkeiten) {
      return [];
    }

    return gueltigkeiten;
  }

  public hasMissingGueltigkeiten(): boolean {
    return !this.skipExperteGueltigkeitsCheck && this.gueltigkeitenToEnforce().length === 0;
  }
}
